import { Box, Grid, GridItem, Hide, Text, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { TicketHolderForm, FormValues } from '../TicketHolderForm/TicketHolderForm';
import { EventSummaryCard } from '../EventSummaryCard/EventSummaryCard';
import { CheckoutSummary } from '../CheckoutSummary/CheckoutSummary';
import { QiCardInstructions } from '../QiCardInstructions/QiCardInstructions';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import type { CalculatePriceResponse } from 'api/responseTypes';
import type { MatchResponse, ConcertResponse } from 'api/responseTypes';

type CheckoutContentProps = {
  matchData?: MatchResponse;
  concertData?: ConcertResponse;
  priceCalculation: CalculatePriceResponse | null;
  isCalculatingPrice?: boolean;
  isLoading: boolean;
  isFreeEvent?: boolean;
  onSubmit: (data: FormValues) => void | Promise<void>;
  onPriceCalculated: (response: CalculatePriceResponse) => void;
  onPromoCodeRemoved: () => void;
  onAuthRequired?: () => void;
  onOpenQiCardInstructionsModal: () => void;
  onOpenSmartphoneModal: () => void;
};

export const CheckoutContent = ({
  matchData,
  concertData,
  priceCalculation,
  isCalculatingPrice,
  isLoading,
  isFreeEvent,
  onSubmit,
  onPriceCalculated,
  onPromoCodeRemoved,
  onAuthRequired,
  onOpenQiCardInstructionsModal,
}: CheckoutContentProps) => {
  const { userToken, authCode } = useAuthCode();
  const termAndConditions = matchData?.termAndConditions ?? concertData?.termAndConditions ?? [];

  return (
    <Box width='100%' px={{ base: 4, md: 8 }} pt={{ base: '16px', md: '32px' }}>
      <Grid
        dir='rtl'
        mx='auto'
        maxW='1200px'
        templateColumns={{ base: '1fr', md: '1fr 360px' }}
        gap='24px'
        alignItems='start'
      >
        {/* Main column (right in RTL): event summary + per-ticket holder cards. */}
        <GridItem>
          <VStack spacing='16px' align='stretch'>
            <EventSummaryCard matchData={matchData} concertData={concertData} />
            <Box>
              <TicketHolderForm onSubmit={onSubmit} onAuthRequired={onAuthRequired} />
            </Box>
          </VStack>
          {!isFreeEvent && (
            <Hide>
              {userToken === undefined && authCode === null && (
                <VStack mt='24px' align='end' spacing='24px'>
                  <Text variant='bodyLargestRegular'>
                    <FormattedMessage
                      id='checkout.paymentDetails'
                      defaultMessage={'Payment Details'}
                    />
                  </Text>
                  <QiCardInstructions onModalOpen={onOpenQiCardInstructionsModal} />
                </VStack>
              )}
            </Hide>
          )}
        </GridItem>

        {/* Sidebar (left in RTL): price + terms + CTA. Sticky on desktop. */}
        <GridItem position={{ md: 'sticky' }} top={{ md: '24px' }}>
          <CheckoutSummary
            termAndConditions={termAndConditions}
            priceCalculation={priceCalculation}
            isCalculatingPrice={isCalculatingPrice}
            isLoading={isLoading}
            isFreeEvent={isFreeEvent}
            onPriceCalculated={onPriceCalculated}
            onPromoCodeRemoved={onPromoCodeRemoved}
            onAuthRequired={onAuthRequired}
          />
        </GridItem>
      </Grid>
    </Box>
  );
};
